import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { PlusIcon, MagnifyingGlassIcon, PencilIcon, TrashIcon, ArrowUpIcon, ArrowDownIcon, EyeIcon } from '@heroicons/react/24/outline';
import axios from '../../api/axiosConfig';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Pagination from '../../components/common/Pagination';

// Interface for Contact data (including optional Client info)
interface Contact {
  id: number;
  name: string;
  phone: string;
  brand?: string;
  email?: string;
  clientId?: number;
  Client?: { // Optional Client object from include
    id: number;
    name: string;
  };
  createdAt?: string;
  updatedAt?: string;
}

// Interface for the API response structure
interface PaginatedContactsResponse {
  contacts: Contact[];
  totalPages: number;
  currentPage: number;
  totalCount: number;
}

const ContactList: React.FC = () => {
  const { can } = useAuth(); // Changed from isAllowed
  const [searchParams, setSearchParams] = useSearchParams();

  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // --- State for Pagination, Sorting, Filtering ---
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get('page')) || 1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [limit] = useState(15);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [sortBy, setSortBy] = useState(searchParams.get('sortBy') || 'name'); // Default sort
  const [sortOrder, setSortOrder] = useState(searchParams.get('sortOrder') || 'ASC'); // Default order

  // Permissions (assuming similar to client management)
  const canViewContacts = can('contacts.view');
  // const canCreateContacts = can('contacts.create'); // Will use can('contacts.create') directly

  // --- Fetch Contacts Function with Params ---
  const fetchContacts = useCallback(async (page = currentPage, search = searchTerm, sort = sortBy, order = sortOrder) => {
    if (!canViewContacts) {
        setError("У вас нет прав для просмотра контактов.");
        setLoading(false);
        return;
    }
    setLoading(true);
    setError(null);

    const params = new URLSearchParams();
    params.append('page', String(page));
    params.append('limit', String(limit));
    params.append('sortBy', sort);
    params.append('sortOrder', order);
    if (search) params.append('search', search);

    setSearchParams(params, { replace: true }); // Update URL

    try {
      const response = await axios.get<PaginatedContactsResponse>('/contacts', { params });
      setContacts(response.data.contacts);
      setTotalPages(response.data.totalPages);
      setCurrentPage(response.data.currentPage);
      setTotalCount(response.data.totalCount);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      setError('Ошибка при загрузке данных о контактах');
      setContacts([]);
      setTotalPages(1);
      setCurrentPage(1);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [limit, setSearchParams, currentPage, searchTerm, sortBy, sortOrder, canViewContacts]); // Add dependencies

  // Trigger fetch when parameters change
  useEffect(() => {
    fetchContacts(currentPage, searchTerm, sortBy, sortOrder);
  }, [fetchContacts, currentPage, searchTerm, sortBy, sortOrder]);

  // --- Handlers ---
  const handleDelete = async (id: number) => {
    if (!can('contacts.delete')) return; // Changed from canManageContacts
    if (window.confirm('Вы уверены, что хотите удалить этот контакт?')) {
      try {
        await axios.delete(`/contacts/${id}`);
        // Refetch current page data after deletion
        fetchContacts(currentPage, searchTerm, sortBy, sortOrder);
      } catch (error) {
        console.error('Error deleting contact:', error);
        alert('Ошибка при удалении контакта');
      }
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1); // Reset to first page on search
  };

  const handleSort = (newSortBy: string) => {
    const newSortOrder = sortBy === newSortBy && sortOrder === 'ASC' ? 'DESC' : 'ASC';
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
    setCurrentPage(1); // Reset to first page on sort change
  };

  // --- Helper Functions ---
  const renderSortIcon = (columnName: string) => {
    if (sortBy !== columnName) {
      return null;
    }
    return sortOrder === 'ASC' ? (
      <ArrowUpIcon className="h-4 w-4 ml-1 inline-block text-gray-600" />
    ) : (
      <ArrowDownIcon className="h-4 w-4 ml-1 inline-block text-gray-600" />
    );
  };

  // --- Render Logic ---
  if (loading && contacts.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div>
      <PageHeader
        title="Контакты"
        subtitle={`Всего найдено: ${totalCount}`}
        actions={
          can('contacts.create') ? ( // Changed from canManageContacts
            <Link
              to="/contacts/new"
              className="btn-primary flex items-center"
            >
              <PlusIcon className="h-5 w-5 mr-1" />
              Добавить контакт
            </Link>
          ) : null
        }
      />

      {error && (
        <div className="bg-red-100 text-red-700 p-4 rounded-md mb-4">
          {error}
        </div>
      )}

      {/* Filters Row */}
      <div className="mb-6 flex flex-wrap gap-4 items-end">
        {/* Search Box */}
        <div className="flex-grow sm:flex-grow-0 sm:w-64">
            <label htmlFor="search" className="form-label">Поиск</label>
            <div className="relative">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                    id="search"
                    type="text"
                    placeholder="Имя, телефон, бренд..."
                    className="form-input pl-10"
                    value={searchTerm}
                    onChange={handleSearchChange}
                />
            </div>
        </div>
      </div>

      {/* Contacts table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full table-fixed divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {/* Sortable Headers */}
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('name')}>
                  Имя {renderSortIcon('name')}
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('phone')}>
                  Телефон {renderSortIcon('phone')}
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('brand')}>
                  Бренд {renderSortIcon('brand')}
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('email')}>
                  Email {renderSortIcon('email')}
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('Client.name')}>
                  Клиент {renderSortIcon('Client.name')}
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Действия
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading && contacts.length > 0 && ( // Show subtle loading row
                <tr><td colSpan={5} className="p-4 text-center text-gray-500"><LoadingSpinner /> Загрузка...</td></tr>
              )}
              {!loading && contacts.length === 0 && (
                <tr><td colSpan={5} className="p-6 text-center text-gray-500">Контакты не найдены</td></tr>
              )}
              {!loading && contacts.map((contact) => (
                <tr key={contact.id} className="hover:bg-gray-50">
                  {/* Removed text-ellipsis, added break-words */}
                  <td className="px-6 py-4 font-medium text-gray-900 break-words text-xs sm:text-sm md:text-base">
                    {/* Link to contact details page (to be created) */}
                    {/* <Link to={`/contacts/${contact.id}`} className="text-primary hover:underline"> */}
                      {contact.name}
                    {/* </Link> */}
                  </td>
                  {/* Removed text-ellipsis */}
                  <td className="px-6 py-4 text-sm text-gray-900 truncate max-w-[200px]">
                    {contact.phone}
                  </td>
                  {/* Removed text-ellipsis, added break-words */}
                  <td className="px-6 py-4 text-sm text-gray-500 break-words">
                    {contact.brand || '-'}
                  </td>
                  {/* Removed text-ellipsis, added break-words */}
                  <td className="px-6 py-4 text-sm text-gray-500 break-words">
                    {contact.email || '-'}
                  </td>
                  {/* Removed text-ellipsis, added break-words */}
                  <td className="px-6 py-4 text-sm text-gray-500 break-words">
                    {contact.Client ? (
                      <Link to={`/clients/${contact.Client.id}`} className="text-primary hover:underline">
                        {contact.Client.name}
                      </Link>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="px-6 py-4 text-right text-sm font-medium">
                    <div className="flex justify-end space-x-2">
                      {/* View Details Button (Optional) */}
                      {/* <Link to={`/contacts/${contact.id}`} className="text-primary hover:text-primary-dark p-1" title="Детали"><EyeIcon className="h-5 w-5" /></Link> */}
                      {can('contacts.edit') && ( // Changed from canManageContacts
                        <Link
                          to={`/contacts/edit/${contact.id}`}
                          className="text-gray-600 hover:text-gray-900 p-1"
                          title="Редактировать"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </Link>
                      )}
                      {can('contacts.delete') && ( // Changed from canManageContacts
                        <button
                          onClick={() => handleDelete(contact.id)}
                          className="text-red-600 hover:text-red-800 p-1"
                          title="Удалить"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
            <div className="p-4 border-t border-gray-200">
              <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
              />
            </div>
        )}
      </div>
    </div>
  );
};

export default ContactList;
